// pdf.js plumbing for the file importer: open a PDF, walk its pages, and hand
// each page's text runs to `pageToSentences` in pdftext.ts, which does the
// runs → lines → paragraphs → sentences work.
//
// Everything here runs in the browser; pdf.js parses in its own worker.
import type { PDFDocumentProxy } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';

import { pageToSentences } from './pdftext';

type PdfJs = typeof import('pdfjs-dist');
let pdfjsPromise: Promise<PdfJs> | null = null;

/**
 * pdf.js is by far the heaviest thing this app can load, and most sessions
 * never open a PDF — so it is fetched on the first import rather than shipped
 * in the initial bundle. (`workerUrl` is only a string, so it stays static.)
 */
function loadPdfJs(): Promise<PdfJs> {
  pdfjsPromise ??= import('pdfjs-dist').then((mod) => {
    mod.GlobalWorkerOptions.workerSrc = workerUrl;
    return mod;
  });
  return pdfjsPromise;
}

// Predefined Adobe CMaps, served by the `s2r-pdf-cmaps` plugin in
// vite.config.ts. A PDF that uses one — common in Korean documents — extracts
// as empty or garbled text without them.
const CMAP_URL = new URL('pdfjs/cmaps/', document.baseURI).href;

export type PdfErrorCode = 'password' | 'corrupt' | 'no_text';

export class PdfError extends Error {
  code: PdfErrorCode;
  constructor(code: PdfErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'PdfError';
    this.code = code;
  }
}

export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

export interface PdfProgress {
  page: number;
  pages: number;
}

/**
 * Extract a PDF's text as one sentence per line, ready to drop into the
 * Add Sentences textarea.
 */
export async function extractPdfText(
  file: File,
  onProgress?: (p: PdfProgress) => void,
): Promise<string> {
  const data = new Uint8Array(await file.arrayBuffer());
  const pdfjs = await loadPdfJs();

  const task = pdfjs.getDocument({ data, cMapUrl: CMAP_URL, cMapPacked: true });
  let doc: PDFDocumentProxy;
  try {
    doc = await task.promise;
  } catch (e) {
    // pdf.js signals an encrypted document with a dedicated exception name.
    if ((e as { name?: string })?.name === 'PasswordException') {
      throw new PdfError('password');
    }
    throw new PdfError('corrupt', String(e));
  }

  try {
    const sentences: string[] = [];
    for (let n = 1; n <= doc.numPages; n++) {
      onProgress?.({ page: n, pages: doc.numPages });
      const page = await doc.getPage(n);
      try {
        const content = await page.getTextContent();
        sentences.push(...pageToSentences(content.items));
      } finally {
        page.cleanup();
      }
    }
    if (!sentences.length) throw new PdfError('no_text');
    return sentences.join('\n');
  } finally {
    // Tears down the worker too — the document proxy alone cannot release it.
    void task.destroy();
  }
}
